import { Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import isEqual from "lodash.isequal";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchTAEDSnapshotByReferenceNumber, fetchTAEDSnapshotsByStationAndDate } from "../api/taed";
import { isUserAuthorizedByServer } from "../api/userAccess";
import PageLoading from "../components/PageLoading";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import WarningDialog from "../components/fundamentals/WarningDialog";
import TemporaryAssignmentDetailDialog from "../components/taed/TemporaryAssignmentDetailDialog";
import TemporaryAssignmentFilterForm from "../components/taed/TemporaryAssignmentFilterForm";
import TemporaryAssignmentTable from "../components/taed/TemporaryAssignmentTable";
import {
  selectTempAssignmentData,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateIncomingOutgoingFilters,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
  updateTempAssignmentFoundByReferenceNumberSearch,
  updateTempAssignmentFoundByReferenceNumberSearchIsDialogOpen,
  updateTempAssignmentFoundByReferenceNumberSearchIsLoading,
} from "../slices/tempAssignment/tempAssignmentSlice";
import { selectStationFilter, selectUserSession, selectWorkgroupFilters, updateStationFilter, updateWorkgroupFilters } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../util/authentication";
import { getAdminStation } from "../util/commonUtils";
import { BREADCRUMBS_TITLE, ERROR_MAX_PAYLOAD_LIMIT_MSG, FEATURES, HOME_PATH } from "../util/constants";
import { TWO_MONTHS_IN_PAST } from "../util/dateHelper";
import {
  deserializeDateFromRedux,
  getIsoDateFormat,
  serializeDateToRedux,
  set_time_to_end_of_day,
} from "../util/dateTimeFormatter";
import { tempAssignmentDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  YOU_CANNOT_EDIT_TEMPORARY_ASSIGNMENT_OVER_TWO_MONTHS_IN_PAST,
} from "../util/messageConstants";
import {
  DEFAULT_TEMP_ASSIGNMENT_END_DATE_FILTER,
  DEFAULT_TEMP_ASSIGNMENT_START_DATE_FILTER,
  filterTempAssignments,
} from "../util/taed/filterTempAssignments";
import { initializeSearchIndex, searchTempAssignments } from "../util/taed/serachTempAssignments";
import {
  getScrubbedTempAssignmentSnapshot,
  getScrubbedTempAssignmentSnapshots,
} from "../util/taed/tempAssignmentScrubber";
import { validateTAEDEndDateFilter, validateTAEDStartDateFilter } from "../util/temporaryAssignmentInputValidation";
import { mergeWebSocketAdminTAEDs } from "../util/webSocketUtils";

const TemporaryAssignmentAdministrationPage = (props) => {
  const dispatch = useDispatch();
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const tempAssignmentData = useSelector(selectTempAssignmentData);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const history = useHistory();
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const [stationTempAssignmentList, setStationTempAssignmentList] = useState([]);
  const [filteredStationTempAssignmentList, setFilteredStationTempAssignmentList] = useState([]);
  const [searchedTempAssignments, setSearchedTempAssignments] = useState([]);
  const [isSearch, setIsSearch] = useState(false);
  const [isErrorDialogopen, setErrorDialogOpen] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});

  const getStartDateFilterSafely = () => {
    return tempAssignmentData?.startDateFilter
      ? deserializeDateFromRedux(tempAssignmentData.startDateFilter)
      : DEFAULT_TEMP_ASSIGNMENT_START_DATE_FILTER(stationFilter);
  };

  const getEndDateFilterSafely = () => {
    return tempAssignmentData?.endDateFilter
      ? deserializeDateFromRedux(tempAssignmentData.endDateFilter)
      : DEFAULT_TEMP_ASSIGNMENT_END_DATE_FILTER(stationFilter);
  };

  // Ref's are used to maintain filter changes synchronously
  const dateTypeFilterRef = useRef(tempAssignmentData.dateTypeFilter);
  const startDateFilterRef = useRef(getStartDateFilterSafely());
  const endDateFilterRef = useRef(getEndDateFilterSafely());
  const stationFilterRef = useRef(stationFilter);
  const incomingOutgoingFiltersRef = useRef(tempAssignmentData.incomingOutgoingFilters);
  const statusFiltersRef = useRef(tempAssignmentData.statusFilters);
  const workgroupFiltersRef = useRef(workgroupFilters);
  const tempWebSocketDataRef = useRef({});
  const tableDesktopRef = useGridApiRef();
  const isApiSubscribedRef = useRef(true);
  const isComponentMounted = useRef(false);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (tempAssignmentData.startDateFilter === null) {
        initializeFilterState();
      }
      if (isFilterValidationSuccess()) {
        updateStationTempAssignmentList(
          stationFilterRef.current,
          dateTypeFilterRef.current,
          startDateFilterRef.current,
          endDateFilterRef.current,
          incomingOutgoingFiltersRef.current
        );
      }
    }

    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUserAuthorizedState,
    tempAssignmentData.dateTypeFilter,
    tempAssignmentData.startDateFilter,
    tempAssignmentData.endDateFilter,
    stationFilter,
    tempAssignmentData.incomingOutgoingFilters,
  ]);

  // Client-side filter logic useEffect
  useEffect(() => {
    if (isComponentMounted.current) {
      const filteredList = filterTempAssignments(
        stationTempAssignmentList,
        dateTypeFilterRef.current,
        startDateFilterRef.current,
        endDateFilterRef.current,
        stationFilterRef.current,
        incomingOutgoingFiltersRef.current,
        statusFiltersRef.current,
        workgroupFiltersRef.current
      );
      applyFilters(filteredList);
      if (tempAssignmentData.searchString !== "") {
        handleSearchSubmit(tempAssignmentData.searchString);
      }
      applyTableScrollToTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationTempAssignmentList, tempAssignmentData.statusFilters, workgroupFilters, stationFilter]);

  useEffect(() => {
    tempWebSocketDataRef.current = webSocketEventData.taedAdminEvents;
    if (Object.values(webSocketEventData.taedAdminEvents).length > 0) {
      if (!isFirstLoad) {
        setStationTempAssignmentList((prevState) => {
          return mergeWebSocketAdminTAEDs(webSocketEventData.taedAdminEvents, stationFilterRef.current, prevState);
        });
        tempWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.taedAdminEvents]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      isComponentMounted.current = true;
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const getCreateTempAssignmentPath = () => {
    const tempAssignmentChildItems = props.featureConfig.childItems;
    const tempAssignment = tempAssignmentChildItems.find((item) => item.key === FEATURES.CREATE_TEMPORARY_ASSIGNMENT);
    return tempAssignment.path;
  };

  // // We need this initialization since these fields are dependent on the stationFilter value
  const initializeFilterState = () => {
    dispatch(updateStartDateFilter(serializeDateToRedux(DEFAULT_TEMP_ASSIGNMENT_START_DATE_FILTER(stationFilter))));
    dispatch(updateEndDateFilter(serializeDateToRedux(DEFAULT_TEMP_ASSIGNMENT_END_DATE_FILTER(stationFilter))));
  };

  const updateStationTempAssignmentList = (station, dateType, startDate, endDate, incomingOutgoingFilters) => {
    isApiSubscribedRef.current = true;
    setIsLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    incomingOutgoingFiltersRef.current = incomingOutgoingFilters;
    fetchTAEDSnapshotsByStationAndDate(station, dateType, startDate, endDate, incomingOutgoingFilters)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(tempAssignmentDataErrorCodeToErrorMessageMap.maxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            let scrubbedTempAssignments = getScrubbedTempAssignmentSnapshots(result.tempAssignmentSnapshots, station);
            setStationTempAssignmentList(scrubbedTempAssignments);
            setStationTempAssignmentList(
              mergeWebSocketAdminTAEDs(tempWebSocketDataRef.current, stationFilterRef.current, scrubbedTempAssignments)
            );
            tempWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(tempAssignmentDataErrorCodeToErrorMessageMap.unexpectedError);
      });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      (responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.startDate === getIsoDateFormat(startDateFilterRef.current)) &&
      (responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString() ||
        responseQueryParams.endDate === getIsoDateFormat(endDateFilterRef.current)) &&
      isEqual(responseQueryParams.incomingOutgoingFilters, incomingOutgoingFiltersRef.current)
    );
  };

  const applyTableScrollToTop = () => {
    const currentDesktop = tableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const isFilterValidationSuccess = () => {
    return (
      dateTypeFilterRef.current !== "" &&
      tempAssignmentData.startDateFilterErrors === "" &&
      tempAssignmentData.endDateFilterErrors === "" &&
      stationFilterRef.current !== "" &&
      incomingOutgoingFiltersRef.current.length > 0 &&
      statusFiltersRef.current.length > 0 &&
      workgroupFiltersRef.current.length > 0 &&
      tempAssignmentData.searchStringErrors === ""
    );
  };

  const handleDateTypeFilterChange = (event) => {
    dateTypeFilterRef.current = event.target.value;
    dispatch(updateDateTypeFilter(event.target.value));
    applyTableScrollToTop();
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateTAEDStartDateFilter(
      date,
      deserializeDateFromRedux(tempAssignmentData.endDateFilter),
      stationFilter
    );
    startDateFilterRef.current = date;
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));

    if (newStartDateFilterErrors === "") {
      dispatch(updateEndDateFilterErrors(""));
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateTAEDEndDateFilter(
      date,
      deserializeDateFromRedux(tempAssignmentData.startDateFilter),
      stationFilter
    );
    endDateFilterRef.current = date;
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));

    if (newEndDateFilterErrors === "") {
      dispatch(updateStartDateFilterErrors(""));
    }
  };

  const handleStationFilterChange = (event) => {
    stationFilterRef.current = event.target.value;
    dispatch(updateStationFilter(event.target.value));
  };

  const handleIncomingOutgoingFilterChange = (event) => {
    incomingOutgoingFiltersRef.current = event.target.value;
    dispatch(updateIncomingOutgoingFilters(event.target.value));
  };

  const handleStatusFilterChange = (event) => {
    statusFiltersRef.current = event.target.value;
    dispatch(updateStatusFilters(event.target.value));
  };

  const handleWorkgroupFilterChange = (event) => {
    workgroupFiltersRef.current = event.target.value;
    dispatch(updateWorkgroupFilters(event.target.value));
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(tempAssignmentData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (searchString) => {
    dispatch(updateSearchStringErrors(""));
    const trimmedSearchString = searchString.trim();
    if (
      !isNaN(parseInt(trimmedSearchString)) &&
      parseInt(trimmedSearchString) >= 10000000 &&
      parseInt(trimmedSearchString) <= 99999999
    ) {
      dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsLoading(true));
      fetchTAEDSnapshotByReferenceNumber(trimmedSearchString, userSession.isAdmin, stationFilter)
        .then((result) => {
          if (result.message) {
            dispatch(updateSearchStringErrors(result.message));
            dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsLoading(false));
          } else {
            let tempAssignmentSnapshot = result.taedSnapshot;
            let scrubbedTempAssignmentSnapshot = getScrubbedTempAssignmentSnapshot(
              tempAssignmentSnapshot,
              stationFilter
            );
            dispatch(updateTempAssignmentFoundByReferenceNumberSearch(tempAssignmentSnapshot));
            dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsLoading(false));
            applySearch(trimmedSearchString, [scrubbedTempAssignmentSnapshot]);
            dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsDialogOpen(true));
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsDialogOpen(false));
          dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsLoading(false));
        });
    } else {
      searchTempAssignments(tempAssignmentData.searchString, applySearch);
    }
  };

  const applyFilters = (filteredTempAssignments) => {
    initializeSearchIndex(filteredTempAssignments);

    if (isSearch) {
      searchTempAssignments(tempAssignmentData.searchString, applySearch);
    }

    setFilteredStationTempAssignmentList(filteredTempAssignments);
  };

  const applySearch = (searchString, searchedTempAssignments) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedTempAssignments(searchedTempAssignments);
      applyTableScrollToTop();
      setIsLoading(false);
    } else {
      setIsSearch(true);
      setSearchedTempAssignments(searchedTempAssignments);
      applyTableScrollToTop();
      setIsLoading(false);
    }
  };
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.TEMPORARY_ASSIGNMENT_ADMINISTRATION];
  const breadcrumbsLinks = [HOME_PATH];

  const handleTempAssignmentDetailDialogCloseClick = () => {
    dispatch(updateTempAssignmentFoundByReferenceNumberSearchIsDialogOpen(false));
  };
  const editTempAssignment = (tempAssignment) => {
    const editTempAssignmentData = getScrubbedTempAssignmentSnapshot(tempAssignment);
    const tempAssignmentChildItems = props.featureConfig.childItems;
    const editTempAssignment = tempAssignmentChildItems.find(
      (item) => item.key === FEATURES.CREATE_TEMPORARY_ASSIGNMENT
    );
    if (editTempAssignmentData.endDate < TWO_MONTHS_IN_PAST(stationFilter)) {
      setErrorDialogOpen(true);
    } else {
      history.push({ pathname: editTempAssignment.path, state: editTempAssignmentData });
    }
  };
  const handleClickDialogClose = () => {
    setErrorDialogOpen(false);
  };
  const renderDesktopTemporaryAssignmentPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={false}
        />
        <TemporaryAssignmentFilterForm
          title="Temporary Assignment Administration"
          onDateTypeFilterChange={handleDateTypeFilterChange}
          startDateFilter={getStartDateFilterSafely()}
          onStartDateFilterChange={handleStartDateFilterChange}
          endDateFilter={getEndDateFilterSafely()}
          onEndDateFilterChange={handleEndDateFilterChange}
          stationFilter={getAdminStation(props.featureConfig.userStations, userSession, stationFilter)}
          onStationFilterChange={handleStationFilterChange}
          onIncomingOutgoingFilterChange={(event) => handleIncomingOutgoingFilterChange(event)}
          onStatusFilterChange={(event) => handleStatusFilterChange(event)}
          onWorkgroupFilterChange={(event) => handleWorkgroupFilterChange(event)}
          onSearchFilterChange={(event) => handleSearchChange(event)}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchBlur={handleSearchBlur}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
        />
        <Button
          variant="contained"
          color="primary"
          href={`#${getCreateTempAssignmentPath()}`}
          sx={{ height: "40px", float: "right", marginRight: "5px", marginBottom: "10px" }}
        >
          <Typography variant="body2" sx={{ color: "secondary.swaWhite" }}>
            Create Temporary Assignment
          </Typography>
        </Button>

        <TemporaryAssignmentTable
          station={getAdminStation(props.featureConfig.userStations, userSession, stationFilter)}
          tempAssignmentList={isSearch ? searchedTempAssignments : filteredStationTempAssignmentList}
          isLoading={isLoading}
          isError={isError}
          tableDesktopRef={tableDesktopRef}
          errorObject={errorObject}
          featureConfig={props.featureConfig}
        />
        {tempAssignmentData.tempAssignmentFoundByReferenceNumberSearchIsDialogOpen && (
          <TemporaryAssignmentDetailDialog
            tempAssignmentReferenceNumber={
              getScrubbedTempAssignmentSnapshot(
                tempAssignmentData.tempAssignmentFoundByReferenceNumberSearch,
                stationFilter
              ).referenceNumber
            }
            isDialogOpen={tempAssignmentData.tempAssignmentFoundByReferenceNumberSearchIsDialogOpen}
            handleClickDialogClose={handleTempAssignmentDetailDialogCloseClick}
            handleEditClick={() => editTempAssignment(tempAssignmentData.tempAssignmentFoundByReferenceNumberSearch)}
          />
        )}
        {isErrorDialogopen && (
          <WarningDialog
            isDialogOpen={isErrorDialogopen}
            header="Warning"
            message={YOU_CANNOT_EDIT_TEMPORARY_ASSIGNMENT_OVER_TWO_MONTHS_IN_PAST}
            handleClickDialogClose={() => handleClickDialogClose()}
            severity={"error"}
          />
        )}
      </>
    );
  };

  const renderMobileTemporaryAssignmentPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents" data-testid="admin-trades-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : props.isDesktopView ? (
        renderDesktopTemporaryAssignmentPage()
      ) : (
        renderMobileTemporaryAssignmentPage()
      )}
    </div>
  );
};

export default TemporaryAssignmentAdministrationPage;
